import { Filter } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { FilterFormData } from '@/pages/filters/hooks/useFilterMutations';
import { mdiCheck, mdiClose, mdiFilter } from '@mdi/js';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface FilterFormProps {
  filter?: Filter | null;
  onSubmit: (data: FilterFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const getFormValues = (filter?: Filter | null): FilterFormData => ({
  name: filter?.name || '',
  enabled: filter?.enabled ?? true,
  isMandatory: filter?.isMandatory ?? false,
});

export const FilterForm: React.FC<FilterFormProps> = ({
  filter,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!filter;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm<FilterFormData>({
    defaultValues: getFormValues(filter),
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (filter) {
      reset(getFormValues(filter));
    }
  }, [filter, reset]);

  const isEnabled = watch('enabled');
  const isMandatory = watch('isMandatory');

  const handleFormSubmit = async (data: FilterFormData) => {
    await onSubmit(data);
  };

  return (
    <div className='p-6'>
      <div className='flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200'>
        <div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
          <div className='w-6 h-6 text-blue-600'>
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d={mdiFilter} />
            </svg>
          </div>
        </div>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>
            {isEditing ? 'Edit Filter' : 'Create New Filter'}
          </h3>
          <p className='text-sm text-gray-600'>
            {isEditing
              ? 'Update filter information'
              : 'Add a new business filter'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
        {/* Filter Information */}
        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-gray-700 mb-4'>
            Filter Information
          </h4>
          <div className='grid grid-cols-1 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Filter Code *
              </label>
              <Controller
                name='name'
                control={control}
                rules={{
                  required: 'Filter code is required',
                  minLength: {
                    value: 2,
                    message: 'Code must be at least 2 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Code must be at most 100 characters',
                  },
                  pattern: {
                    value: /^[a-z0-9_-]+$/,
                    message:
                      'Code must be lowercase with only letters, numbers, underscores, or hyphens',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    onChange={e => field.onChange(e.target.value.toLowerCase())}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder='e.g. wifi, power_sockets, outdoor_seating'
                  />
                )}
              />
              <p className='text-xs text-gray-500 mt-1'>
                Lowercase code matched with statics to display name and icon
              </p>
              <FormErrorMessage message={errors.name?.message} />
            </div>
          </div>
        </div>

        {/* Mandatory Setting */}
        <div
          className={`rounded-lg p-4 ${isMandatory ? 'bg-purple-50' : 'bg-gray-50'}`}
        >
          <h4 className='text-sm font-medium text-gray-700 mb-4'>
            Filter Settings
          </h4>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-gray-900'>
                {isMandatory ? 'Mandatory Filter' : 'Optional Filter'}
              </p>
              <p className='text-xs text-gray-500 mt-1'>
                {isMandatory
                  ? 'This filter is required when creating reviews'
                  : 'This filter is optional for reviews'}
              </p>
            </div>
            <Controller
              name='isMandatory'
              control={control}
              render={({ field }) => (
                <ToggleSwitch value={field.value} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Filter Status - Only show in edit mode */}
        {isEditing && (
          <div
            className={`rounded-lg p-4 ${isEnabled ? 'bg-green-50' : 'bg-red-50'}`}
          >
            <h4 className='text-sm font-medium text-gray-700 mb-4'>
              Filter Status
            </h4>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-900'>
                  {isEnabled ? 'Filter Enabled' : 'Filter Disabled'}
                </p>
                <p className='text-xs text-gray-500 mt-1'>
                  {isEnabled
                    ? 'Filter is visible and can be used for businesses'
                    : 'Filter is hidden and cannot be assigned to businesses'}
                </p>
              </div>
              <Controller
                name='enabled'
                control={control}
                render={({ field }) => (
                  <ToggleSwitch value={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex items-center justify-end space-x-3 pt-6 border-t border-gray-200'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='submit'
            label={isEditing ? 'Update Filter' : 'Create Filter'}
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid || (!isDirty && isEditing)}
          />
        </div>
      </form>
    </div>
  );
};
